import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  History,
  FileBarChart,
  UserCheck,
  Shield,
  BarChart3,
  Sun,
  Moon,
  Trash2,
  Building2,
} from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isRegulator } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(
    location.pathname.includes('/admin/users') || 
    location.pathname.includes('/inspectors') || 
    location.pathname.includes('/regulators')
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-purple-100 text-purple-700',
      inspector: 'bg-teal-100 text-teal-700',
      regulator: 'bg-blue-100 text-blue-700',
    };
    return badges[role] || badges.inspector;
  };

  const mainNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'inspector', 'regulator'] },
    { path: '/records', label: 'Records', icon: FileText, roles: ['admin', 'inspector', 'regulator'] },
    { path: '/inspections', label: 'Inspections', icon: ClipboardCheck, roles: ['admin', 'inspector', 'regulator'] },
    { path: '/performance', label: 'Performance', icon: BarChart3, roles: ['admin', 'inspector', 'regulator'] },
  ];

  const adminNavItems = [
    { path: '/workshops', label: 'Workshops', icon: Building2, roles: ['admin', 'regulator', 'inspector'] },
    { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'regulator'] },
    { path: '/admin/audit-logs', label: 'Audit Log', icon: History, roles: ['admin', 'regulator'] },
    { path: '/admin/deleted-records', label: 'Deleted Records Log', icon: Trash2, roles: ['admin', 'regulator'] },
    { path: '/admin/config', label: 'Settings', icon: Settings, roles: ['admin'] },
  ];

  const NavLink = ({ item, onClick }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || 
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
    
    if (!item.roles.includes(user?.role)) return null;
    
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
          isActive 
            ? "bg-primary/10 text-primary" 
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
      >
        <Icon className="h-4 w-4" />
        {item.label}
        {item.badge && (
          <Badge variant="destructive" className="ml-auto text-xs px-1.5">
            {item.badge}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <span className="font-heading font-semibold">CNG Portal</span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="mobile-theme-toggle"
            className="rounded-full"
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5 text-amber-500" />
            ) : (
              <Moon className="h-5 w-5 text-slate-600" />
            )}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-card border-r transform transition-transform duration-200 lg:translate-x-0 flex flex-col",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="h-14 flex items-center gap-3 px-4 border-b shrink-0">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-sm">CNG Portal</h1>
            <p className="text-[10px] text-muted-foreground">Inspection System</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Main Navigation */}
          {mainNavItems.map((item) => (
            <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
          ))}

          {/* Users Section (Admin only) */}
          {isAdmin && (
            <Collapsible open={usersExpanded} onOpenChange={setUsersExpanded}>
              <CollapsibleTrigger className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                <Users className="h-4 w-4" />
                <span className="flex-1 text-left">Users</span>
                {usersExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1 mt-1">
                <Link
                  to="/admin/users?role=inspector"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                    location.pathname.includes('/admin/users') && location.search.includes('inspector')
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  data-testid="nav-inspectors"
                >
                  <UserCheck className="h-4 w-4" />
                  Inspectors
                </Link>
                <Link
                  to="/admin/users?role=regulator"
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                    location.pathname.includes('/admin/users') && location.search.includes('regulator')
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  data-testid="nav-regulators"
                >
                  <Shield className="h-4 w-4" />
                  Regulators
                </Link>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Admin Navigation */}
          <div className="pt-2 mt-2 border-t">
            {adminNavItems.map((item) => (
              <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="p-3 border-t shrink-0">
          {/* Theme Toggle */}
          <div className="flex items-center justify-between px-2 py-2 mb-2">
            <span className="text-xs text-muted-foreground">Theme</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              data-testid="desktop-theme-toggle"
              className="h-8 px-2"
            >
              {theme === 'dark' ? (
                <div className="flex items-center gap-2">
                  <Sun className="h-4 w-4 text-amber-500" />
                  <span className="text-xs">Light</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Moon className="h-4 w-4 text-slate-600" />
                  <span className="text-xs">Dark</span>
                </div>
              )}
            </Button>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name}</p>
                  <span className={cn(
                    "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase",
                    getRoleBadge(user?.role)
                  )}>
                    {user?.role}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
